import React, { useState, useEffect, useMemo } from 'react';
import { PageHeader } from './PageHeader';
import { useAuthStore } from '@passport/store';
import { HttpClient, UserClient } from '@passport/api-client';
import './pages.css';

export interface ProfilePageProps {
  /**
   * Opt-in. Rejects a blank display name, trims it before saving, and marks the field `aria-invalid`
   * with focus. Off by default so other hosts keep their existing behaviour.
   */
  fieldValidation?: boolean;
}

export const ProfilePage: React.FC<ProfilePageProps> = ({ fieldValidation = false }) => {
  const { user, token, setUser } = useAuthStore();
  // Was '@admin/api's profileService — a private alias only admin-dashboard's own
  // vite.config.ts declares, which silently made this "shared" page unusable by any other
  // app (confirmed: app-template has no @admin/api alias at all). @passport/api-client's
  // UserClient.updateProfile hits the identical self-service/users/me/update endpoint, so
  // this is a like-for-like swap, not a behavior change — just makes the page actually
  // portable. A fresh HttpClient is constructed here (rather than reusing a shared
  // singleton, which this package doesn't export one of) and given the current token,
  // matching the same ad-hoc pattern app-template's own App.tsx already uses for its
  // profile fetch.
  const userClient = useMemo(() => {
    const httpClient = new HttpClient(import.meta.env.VITE_API_BASE_URL || window.location.origin);
    if (token) httpClient.setToken(token);
    return new UserClient(httpClient);
  }, [token]);
  const userId = user ? parseInt(user.id) : 0;

  const [isEditing, setIsEditing] = useState(false);
  const [displayName, setDisplayName] = useState(user?.name || '');
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [saveError, setSaveError] = useState('');
  const [nameInvalid, setNameInvalid] = useState(false);

  useEffect(() => {
    if (user) setDisplayName(user.name || '');
  }, [user]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userId) return;
    const name = fieldValidation ? displayName.trim() : displayName;
    if (fieldValidation && !name) {
      setNameInvalid(true);
      setSaveError('Display name is required.');
      setSaveStatus('error');
      document.getElementById('display-name')?.focus();
      return;
    }
    setNameInvalid(false);
    setIsSaving(true);
    setSaveStatus('idle');
    setSaveError('');
    try {
      await userClient.updateProfile(userId, name);
      if (user) setUser({ ...user, name });
      setSaveStatus('success');
      setIsEditing(false);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save profile');
      setSaveStatus('error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setDisplayName(user?.name || '');
    setIsEditing(false);
    setSaveStatus('idle');
    setSaveError('');
    setNameInvalid(false);
  };

  const initials = (displayName[0] || user?.email?.[0] || 'U').toUpperCase();

  return (
    <div className="admin-page-container">
      <PageHeader title="My Profile" subtitle="View and manage your account information" />

      {saveStatus === 'success' && (
        <div className="page-alert page-alert-success">
          <span className="page-alert-message">Profile updated successfully.</span>
        </div>
      )}

      {/* Avatar summary card */}
      <div className="page-card">
        <div className="page-card-body" style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
          <div
            className="page-avatar"
            role="img"
            aria-label={`${displayName || 'User'} avatar`}
          >
            {initials}
          </div>
          <div>
            <p className="page-card-title" style={{ margin: '0 0 0.25rem 0' }}>
              {displayName || user?.email || 'User'}
            </p>
            <p className="page-text-muted admin-text-small" style={{ margin: '0 0 0.5rem 0' }}>
              {user?.email}
            </p>
            <span className="page-badge page-badge-success">Active</span>
          </div>
        </div>
      </div>

      {/* Account information card */}
      <div className="page-card">
        <div className="page-card-header">
          <h2 className="page-card-title">Account Information</h2>
          {!isEditing && (
            <button type="button" onClick={() => setIsEditing(true)} className="page-btn page-btn-primary">
              Edit Profile
            </button>
          )}
        </div>

        <div className="page-card-body">
          {!isEditing ? (
            <div className="page-table-wrapper">
              <table className="page-table page-info-table">
                <tbody>
                  <tr>
                    <th>Display Name</th>
                    <td>{displayName || '—'}</td>
                  </tr>
                  <tr>
                    <th>Email Address</th>
                    <td>{user?.email || '—'}</td>
                  </tr>
                  <tr>
                    <th>User ID</th>
                    <td className="page-text-muted">{user?.id || '—'}</td>
                  </tr>
                  {user?.roles && user.roles.length > 0 && (
                    <tr>
                      <th>Roles</th>
                      <td>
                        <div className="page-btn-group">
                          {user.roles.map((role: any) => {
                            const label = typeof role === 'string' ? role : role.name;
                            return (
                              <span key={label} className="page-badge page-badge-primary">{label}</span>
                            );
                          })}
                        </div>
                      </td>
                    </tr>
                  )}
                  {user?.permissions && user.permissions.length > 0 && (
                    <tr>
                      <th>Permissions</th>
                      <td>
                        <div className="page-btn-group">
                          {user.permissions.slice(0, 6).map((p: string) => (
                            <span key={p} className="page-badge page-badge-secondary">{p}</span>
                          ))}
                          {user.permissions.length > 6 && (
                            <span className="page-badge page-badge-secondary">
                              +{user.permissions.length - 6} more
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          ) : (
            <form onSubmit={handleSave} className="page-form" noValidate>
              {saveStatus === 'error' && (
                <div className="page-alert page-alert-error" role="alert" id="profile-save-error">
                  <span className="page-alert-message">{saveError}</span>
                </div>
              )}

              <div className="page-form-group">
                <label htmlFor="display-name" className="page-form-label required">
                  Display Name
                </label>
                <input
                  id="display-name"
                  aria-invalid={nameInvalid}
                  aria-describedby={nameInvalid ? 'profile-save-error' : undefined}
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  className="page-form-input"
                  required
                  placeholder="Your display name"
                  disabled={isSaving}
                />
              </div>

              <div className="page-form-group">
                <label htmlFor="profile-email" className="page-form-label">
                  Email Address
                </label>
                <input
                  id="profile-email"
                  type="email"
                  value={user?.email || ''}
                  className="page-form-input"
                  disabled
                />
                <p className="page-form-help">Email address cannot be changed.</p>
              </div>

              <div className="page-btn-group">
                <button type="submit" className="page-btn page-btn-primary" disabled={isSaving}>
                  {isSaving ? 'Saving…' : 'Save Changes'}
                </button>
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="page-btn page-btn-secondary"
                  disabled={isSaving}
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      {/* Security card — link only; the actual form lives at @passport/pages' own
          ChangePasswordPage (uses @passport/store directly, not auth-core's separate
          ChangePasswordPage/store). Plain <a>, matching the same full-page-reload pattern
          this package's other cross-page links already use (e.g. AuthHeaderControls'
          "View App Settings" row) — consuming apps register /change-password in their own
          route map the same way they already do for /profile and /settings. */}
      <div className="page-card">
        <div className="page-card-body" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <p className="admin-text-emphasis" style={{ margin: '0 0 0.25rem 0', color: 'var(--text-primary)' }}>Password</p>
            <p className="page-text-muted admin-text-small" style={{ margin: 0 }}>
              Change the password used to sign in to your account.
            </p>
          </div>
          <a href="/change-password" className="page-btn page-btn-secondary">
            Change Password
          </a>
        </div>
      </div>
    </div>
  );
};
